import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '../../hooks/usePageTitle';
import { DATA_TABLE_TALL_ROW_HEIGHT, useViewportPageSize } from '../../hooks/useViewportPageSize';
import { usePageHeader } from '../../providers/PageHeaderProvider';
import { useIamCatalog, useIamUsersList } from '../../hooks/useIam';
import type { SortOrder } from '../../components/billing/TableControls';
import type { CatalogRoleEntry, IamUserListItem } from '../../services/iam';
import { AccessStateNotice } from '../../components/access/AccessStateNotice';
import { catalogLookups } from '../../components/access/iamLookups';
import {
    AccessUsersTable,
    type AccessUsersSortBy
} from '../../components/access/users/AccessUsersTable';
import { AccessUsersToolbar } from '../../components/access/users/AccessUsersToolbar';
import { UserDrawer } from '../../components/access/users/UserDrawer';

const SEARCH_DEBOUNCE_MS = 300;
// Starting limit only: the fetch waits for the viewport measurement.
const VIEWPORT_DEFAULT_PAGE_SIZE = 20;

export function AccessUsersList() {
    usePageTitle('Access · Users');
    const { setTitle, setSubtitle } = usePageHeader();
    const navigate = useNavigate();
    const { userId } = useParams<{ userId: string }>();

    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    // Array-shaped for FilterDropdown's multi-select contract; the filter is
    // single-valued, read via `roleFilter[0]` below.
    const [roleFilter, setRoleFilter] = useState<string[]>([]);
    const [sortBy, setSortBy] = useState<AccessUsersSortBy>('name');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [search]);

    const { pageSize, isMeasured, tableCardRef, tableHeaderRef, tableBodyRef, paginationRef } =
        useViewportPageSize(VIEWPORT_DEFAULT_PAGE_SIZE, DATA_TABLE_TALL_ROW_HEIGHT);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, roleFilter, pageSize]);

    const handleSort = (column: AccessUsersSortBy) => {
        if (column === sortBy) {
            setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
        setPage(1);
    };

    const { catalog } = useIamCatalog();
    const roles = useMemo(
        () =>
            (catalog?.roles ?? []).filter((role): role is CatalogRoleEntry => role.kind === 'role'),
        [catalog]
    );
    const { roleLookup } = useMemo(() => catalogLookups(catalog), [catalog]);

    const { users, meta, isLoading, isError } = useIamUsersList(
        {
            page,
            limit: pageSize,
            q: debouncedSearch || undefined,
            role: roleFilter[0] || undefined,
            sort: sortBy,
            order: sortOrder
        },
        isMeasured
    );

    const totalPages = Math.max(1, Math.ceil(meta.total / pageSize));

    useEffect(() => {
        setTitle('Users');
        setSubtitle(isError ? undefined : `${meta.total} total`);
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [meta.total, isError, setTitle, setSubtitle]);

    const hasFilters = Boolean(debouncedSearch || roleFilter.length > 0);
    const clearFilters = () => {
        setSearch('');
        setRoleFilter([]);
    };

    const openUser = (user: IamUserListItem) => navigate(`/access/users/${user.id}`);
    const closeDrawer = () => navigate('/access/users');

    // `lastActive` only exists on the list row; `undefined` means "not on
    // the loaded page", which the drawer treats as unknown.
    const openUserRow = userId ? users.find((u) => u.id === userId) : undefined;

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            <div className="px-8 py-4 shrink-0">
                <AccessUsersToolbar
                    search={search}
                    onSearchChange={setSearch}
                    roles={roles}
                    roleFilter={roleFilter}
                    onRoleFilterChange={setRoleFilter}
                />
            </div>

            {isError ? (
                <AccessStateNotice
                    title="Couldn't load the user directory"
                    message="Something went wrong loading GET /api/v2/iam/users. Try again in a moment."
                />
            ) : (
                <AccessUsersTable
                    users={users}
                    roleLookup={roleLookup}
                    isLoading={isLoading}
                    search={debouncedSearch}
                    hasFilters={hasFilters}
                    onClearFilters={clearFilters}
                    onOpenUser={openUser}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    page={page}
                    totalPages={totalPages}
                    total={meta.total}
                    onPage={(p) => setPage(Math.min(Math.max(1, p), totalPages))}
                    tableCardRef={tableCardRef}
                    tableHeaderRef={tableHeaderRef}
                    tableBodyRef={tableBodyRef}
                    paginationRef={paginationRef}
                />
            )}

            {userId && (
                <UserDrawer
                    key={userId}
                    userId={userId}
                    lastActive={openUserRow?.lastActive}
                    roleLookup={roleLookup}
                    onClose={closeDrawer}
                />
            )}
        </div>
    );
}
